import {OnChangeFn} from "@tanstack/react-table";
import {ReactNode} from "react";
import {FiTrash} from "react-icons/fi";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Subscriber, SubscriptionType} from "@/lib/types/statuspages.ts";
import {StatusPageSubscribersDeleteActionButton} from "@/pages/statuspages/tabs/subscribers/actions/StatusPageSubscribersDeleteActionButton.tsx";

import {specs as typeSpecificSpecs} from "./StatusPageSubscribersTable.utils.tsx";

import styles from "./StatusPageSubscribersTable.module.scss";

export interface StatusPageSubscribersTableProps {
  type: Exclude<SubscriptionType, SubscriptionType.RSS>;
  items: Subscriber[];
  rowSelection: Record<string, boolean>;
  setRowSelection: OnChangeFn<Record<string, boolean>>;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  noDataElement: ReactNode;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
  paginationData?: unknown[];
}

export const StatusPageSubscribersTable = ({
  items,
  type,
  rowSelection,
  setRowSelection,
  sortColumn,
  sortDirection,
  onSortingChange,
  noDataElement,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
  paginationData,
}: StatusPageSubscribersTableProps) => {
  const {user} = useCurrentUserContext();
  const specs = [
    ...typeSpecificSpecs[type],
    {
      fieldName: "created_at",
      fieldTitle: "Date Added",
      enableSorting: true,
      accessor: (obj: Subscriber) =>
        obj.created_at ? <TwoLineShortDateTimeUser date={obj.created_at.toString()} user={user!} /> : "",
    },
  ];
  if (user?.has_write_access) {
    specs.push({
      fieldName: "action",
      meta: {
        headerClassName: styles.actionColumn,
      },
      accessor: (obj: Subscriber) => (
        <StatusPageSubscribersDeleteActionButton
          subscriberIds={[obj.id]}
          onSuccess={() => setRowSelection({})}
          variant={"tableRow"}
        >
          <Icon icon={FiTrash} />
        </StatusPageSubscribersDeleteActionButton>
      ),
    });
  }
  const selectedIds = Object.keys(rowSelection)
    .map((k) => items[parseInt(k)]?.id)
    .filter((id) => id);
  return (
    <Table
      rows={items}
      minWidth="800px"
      specs={specs}
      initialState={{columnPinning: {right: ["action"]}}}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      sorting={{column: sortColumn, direction: sortDirection}}
      onSortingChange={onSortingChange}
      noDataComponent={
        <EmptyTable colSpan={4} width="">
          {noDataElement}
        </EmptyTable>
      }
      actionBar={
        <StatusPageSubscribersDeleteActionButton
          subscriberIds={selectedIds}
          isBulkAction={true}
          onSuccess={() => setRowSelection({})}
          variant="tableCrown"
        >
          <Icon icon={FiTrash} />
          Delete
        </StatusPageSubscribersDeleteActionButton>
      }
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationOptions={paginationOptions}
      paginationData={paginationData}
    />
  );
};
